import { createSlice } from '@reduxjs/toolkit'

import * as API from 'api/skills/skills'
import type { SkillData, SkillListResponse, SkillResponse } from 'api/skills/types'

import { handleApiError } from 'slices/utils'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import type { AppThunk, RootState } from 'store'

type SkillState = {
  isRequesting: boolean
  errorMessage: string
  skill?: SkillData
  skills: SkillData[]
}

const initialState: SkillState = {
  isRequesting: false,
  errorMessage: '',
  skill: undefined,
  skills: [],
}

export const skillsSlice = createSlice({
  name: 'skills',
  initialState,
  reducers: {
    startRequest: state => {
      state.isRequesting = true
      state.errorMessage = ''
    },
    clearErrorMessage: state => {
      state.errorMessage = ''
    },
    apiFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isRequesting = false
      state.errorMessage = action.payload.errorMessage
    },
    getSkillListSuccess: (state, action: PayloadAction<SkillListResponse>) => {
      state.isRequesting = false
      state.skills = action.payload.skills
    },
    getSkillSuccess: (state, action: PayloadAction<SkillResponse>) => {
      state.isRequesting = false
      state.skill = action.payload.skill
    },
    createSkillSuccess: state => {
      state.isRequesting = false
    },
    updateSkillSuccess: state => {
      state.isRequesting = false
    },
    deleteSkillSuccess: state => {
      state.isRequesting = false
    },
  },
})

export const {
  startRequest,
  clearErrorMessage,
  apiFailure,
  getSkillListSuccess,
  getSkillSuccess,
  createSkillSuccess,
  updateSkillSuccess,
  deleteSkillSuccess,
} = skillsSlice.actions

export const getSkillList = (): AppThunk => async dispatch => {
  dispatch(startRequest())
  try {
    const res = await API.getSkillList()
    dispatch(getSkillListSuccess(res))
  } catch (res) {
    handleApiError(res as AxiosError, dispatch, apiFailure)
  }
}

export const getSkill =
  (skillId: number): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    try {
      const res = await API.getSkill(skillId)
      dispatch(getSkillSuccess(res))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const createSkill =
  (name: string): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    try {
      await API.createSkill(name)
      dispatch(createSkillSuccess())
      dispatch(getSkillList())
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const updateSkill =
  (skillId: number, name: string): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    try {
      await API.updateSkill(skillId, name)
      dispatch(updateSkillSuccess())
      dispatch(getSkillList())
      dispatch(getSkill(skillId))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const deleteSkill =
  (skillId: number): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    try {
      await API.deleteSkill(skillId)
      dispatch(deleteSkillSuccess())
      dispatch(getSkillList())
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const selectSkillsStatus = (state: RootState) => ({ ...state.skills })

export default skillsSlice.reducer
